import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { UploadCloud, Trash2, RefreshCw, Copy, Eye } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { adminApi, apiError } from "../api";
import { resolveImg } from "../../context/SiteContext";

const FOLDERS = ["hero", "states", "destinations", "packages", "gallery", "blog", "misc"];

export default function Images() {
  const [folder, setFolder] = useState("hero");
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploads, setUploads] = useState([]);
  const [preview, setPreview] = useState(null);
  const inputRef = useRef(null);
  const replaceRef = useRef(null);
  const replaceTarget = useRef(null);

  const load = () => {
    setLoading(true);
    adminApi.get(`/media/list?folder=${folder}`).then(({ data }) => setFiles(data)).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  };

  useEffect(load, [folder]); // eslint-disable-line react-hooks/exhaustive-deps

  const uploadFiles = async (fileList, targetName = null) => {
    const arr = Array.from(fileList);
    for (const file of arr) {
      const key = targetName || file.name;
      setUploads((u) => [...u, { key, pct: 0 }]);
      try {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("folder", folder);
        if (targetName) fd.append("filename", targetName);
        await adminApi.post("/media/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => {
            const pct = Math.round((e.loaded / (e.total || 1)) * 100);
            setUploads((u) => u.map((x) => (x.key === key ? { ...x, pct } : x)));
          },
        });
        toast.success(targetName ? `Replaced ${targetName} — live on the website now` : `Uploaded ${file.name} (optimized + WebP)`);
      } catch (e) {
        toast.error(apiError(e));
      } finally {
        setUploads((u) => u.filter((x) => x.key !== key));
      }
    }
    load();
  };

  const onDelete = async (name) => {
    if (!window.confirm(`Delete ${name}? This cannot be undone.`)) return;
    try {
      await adminApi.delete(`/media/${folder}/${name}`);
      toast.success("Image deleted");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const copyUrl = (f) => {
    navigator.clipboard.writeText(f.webp_url || f.url);
    toast.success("Image URL copied");
  };

  return (
    <div data-testid="admin-images-page">
      <h1 className="text-2xl font-bold mb-1">Image Manager</h1>
      <p className="text-sm text-muted-foreground mb-6">Upload, replace and organize images. Replacing keeps the same filename & URL — changes appear on the website instantly.</p>

      <div className="flex gap-2 flex-wrap mb-6">
        {FOLDERS.map((f) => (
          <button key={f} onClick={() => setFolder(f)} className={`px-4 py-1.5 rounded-full text-sm border capitalize transition-colors ${folder === f ? "bg-emerald-500 text-white border-emerald-500" : "bg-white dark:bg-slate-900 hover:border-emerald-400"}`} data-testid={`images-folder-${f}`}>
            {f}
          </button>
        ))}
      </div>

      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); uploadFiles(e.dataTransfer.files); }}
        onClick={() => inputRef.current?.click()}
        className="border-2 border-dashed rounded-2xl p-10 text-center cursor-pointer bg-white dark:bg-slate-900 hover:border-emerald-400 transition-colors mb-4"
        data-testid="images-dropzone"
      >
        <UploadCloud className="h-10 w-10 mx-auto text-emerald-500 mb-3" />
        <div className="font-medium">Drag & drop images here (bulk supported), or click to browse</div>
        <div className="text-xs text-muted-foreground mt-1">Auto-optimized: resized to max 1920px, compressed, WebP versions generated → "{folder}" folder</div>
      </div>
      <input ref={inputRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => { uploadFiles(e.target.files); e.target.value = ""; }} data-testid="images-file-input" />
      <input ref={replaceRef} type="file" accept="image/*" className="hidden" onChange={(e) => { if (e.target.files?.[0] && replaceTarget.current) uploadFiles([e.target.files[0]], replaceTarget.current); e.target.value = ""; }} data-testid="images-replace-input" />

      {uploads.length > 0 && (
        <div className="mb-4 space-y-2" data-testid="upload-progress-list">
          {uploads.map((u) => (
            <div key={u.key} className="flex items-center gap-3 text-sm">
              <span className="truncate flex-1">{u.key}</span>
              <div className="h-2 w-48 rounded-full bg-muted overflow-hidden">
                <div className="h-full bg-emerald-500 transition-all" style={{ width: `${u.pct}%` }} />
              </div>
              <span className="text-xs text-muted-foreground w-10 text-right">{u.pct}%</span>
            </div>
          ))}
        </div>
      )}

      {loading ? (
        <div className="py-16 text-center text-muted-foreground">Loading…</div>
      ) : files.length === 0 ? (
        <div className="py-16 text-center text-muted-foreground rounded-2xl border border-dashed">No images in "{folder}" yet.</div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {files.map((f) => (
            <div key={f.name} className="rounded-xl overflow-hidden border bg-white dark:bg-slate-900 group" data-testid={`image-card-${f.name}`}>
              <div className="aspect-video overflow-hidden bg-muted">
                <img src={`${resolveImg(f.url)}?v=${f.modified}`} alt={f.name} className="h-full w-full object-cover" loading="lazy" />
              </div>
              <div className="p-3">
                <div className="text-xs font-medium truncate">{f.name}</div>
                <div className="text-[11px] text-muted-foreground">{(f.size / 1024).toFixed(0)} KB {f.webp_url && "· WebP ✓"}</div>
                <div className="flex gap-1 mt-2">
                  <Button size="icon" variant="ghost" className="h-8 w-8" title="Preview" onClick={() => setPreview(f)} data-testid={`image-preview-${f.name}`}><Eye className="h-3.5 w-3.5" /></Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8" title="Replace (keeps filename)" onClick={() => { replaceTarget.current = f.name; replaceRef.current?.click(); }} data-testid={`image-replace-${f.name}`}><RefreshCw className="h-3.5 w-3.5" /></Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8" title="Copy URL" onClick={() => copyUrl(f)} data-testid={`image-copy-${f.name}`}><Copy className="h-3.5 w-3.5" /></Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500" title="Delete" onClick={() => onDelete(f.name)} data-testid={`image-delete-${f.name}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!preview} onOpenChange={(v) => !v && setPreview(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>{preview?.name}</DialogTitle></DialogHeader>
          {preview && <img src={`${resolveImg(preview.url)}?v=${preview.modified}`} alt={preview.name} className="w-full rounded-lg" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
